/**
 * ListingFilters Component
 *
 * Filtering controls for the admin listings list.
 */

import React from 'react';
import { AdminListingsFilter } from '../hooks';

interface ListingFiltersProps {
  filter: AdminListingsFilter;
  onChange: (filter: AdminListingsFilter) => void;
  totalCount: number;
}

const propertyTypes = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'chalet', label: 'Chalet' },
  { value: 'traditional_house', label: 'Traditional House' },
  { value: 'studio', label: 'Studio' },
  { value: 'hotel_room', label: 'Hotel Room' },
];

const locations = [
  'Beirut',
  'Mount Lebanon',
  'North',
  'South',
  'Bekaa Valley',
  'Byblos',
];

export const ListingFilters: React.FC<ListingFiltersProps> = ({
  filter,
  onChange,
  totalCount,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title, host, or location..."
              value={filter.search || ''}
              onChange={(e) => onChange({ ...filter, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Status Filter */}
        <select
          value={filter.status || ''}
          onChange={(e) =>
            onChange({
              ...filter,
              status: e.target.value as AdminListingsFilter['status'],
            })
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending Review</option>
          <option value="paused">Paused</option>
          <option value="draft">Draft</option>
        </select>

        {/* Property Type Filter */}
        <select
          value={filter.propertyType || ''}
          onChange={(e) => onChange({ ...filter, propertyType: e.target.value })}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">All Types</option>
          {propertyTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        {/* Location Filter */}
        <select
          value={filter.location || ''}
          onChange={(e) => onChange({ ...filter, location: e.target.value })}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">All Locations</option>
          {locations.map((location) => (
            <option key={location} value={location}>
              {location}
            </option>
          ))}
        </select>

        {/* Featured Filter */}
        <select
          value={
            filter.isFeatured === true
              ? 'true'
              : filter.isFeatured === false
              ? 'false'
              : ''
          }
          onChange={(e) =>
            onChange({
              ...filter,
              isFeatured:
                e.target.value === 'true'
                  ? true
                  : e.target.value === 'false'
                  ? false
                  : '',
            })
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="">Featured?</option>
          <option value="true">Featured</option>
          <option value="false">Not Featured</option>
        </select>

        {/* Sort */}
        <select
          value={`${filter.sortBy || 'createdAt'}-${filter.sortOrder || 'desc'}`}
          onChange={(e) => {
            const [sortBy, sortOrder] = e.target.value.split('-') as [
              AdminListingsFilter['sortBy'],
              AdminListingsFilter['sortOrder']
            ];
            onChange({ ...filter, sortBy, sortOrder });
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
        >
          <option value="createdAt-desc">Newest First</option>
          <option value="createdAt-asc">Oldest First</option>
          <option value="title-asc">Title A-Z</option>
          <option value="title-desc">Title Z-A</option>
          <option value="bookingsCount-desc">Most Bookings</option>
          <option value="nightlyRate-desc">Highest Price</option>
          <option value="nightlyRate-asc">Lowest Price</option>
        </select>
      </div>

      {/* Results count */}
      <div className="mt-3 text-sm text-gray-500">
        {totalCount} listing{totalCount !== 1 ? 's' : ''} found
      </div>
    </div>
  );
};

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

export default ListingFilters;
